import React, { useCallback, Fragment, PropsWithChildren } from "react";
import {
  ScrollView,
  View,
  LayoutChangeEvent,
  StyleProp,
  ViewStyle,
} from "react-native";
import { Text } from "react-native-elements";
import { fonts, UNIT, MAX_CONTENT_WIDTH, colors } from "../styles";
import { Persona } from "../types";
import Header from "./Header";
import LeftNav from "./LeftNav";
import { useRoute } from "@react-navigation/native";
import { footerElements } from "../footer";
import { useRecoilState } from "recoil";
import { MOBILE_WIDTH_THRESHOLD, mobileModeState } from "../state/mobileMode";
import { leftNavOpenState } from "../state/leftNav";

const contentShared: StyleProp<ViewStyle> = {
  flex: 1,
  flexDirection: "column",
};

const styles = {
  content: {
    ...contentShared,
    paddingLeft: UNIT * 4,
    paddingRight: UNIT * 2,
  },
  contentMobile: {
    ...contentShared,
    paddingLeft: UNIT,
    paddingRight: UNIT,
    minWidth: 320,
    overview: "hidden",
  },
  headerContainer: {},
  breadcrumbs: {
    fontFamily: fonts.smallHeader,
    top: 3,
    fontSize: 18,
    lineHeight: UNIT * 4,
    marginBottom: UNIT,
  },
  screen: {
    flex: 1,
    height: "100%",
    maxWidth: "100%",
  },
  screenBody: {
    flex: 1,
    flexDirection: "row",
  },
  contentScroll: {
    minHeight: "100%",
    maxWidth: MAX_CONTENT_WIDTH,
    flex: 1,
  },
  footer: {
    marginTop: UNIT * 2,
    marginBottom: UNIT * 2,
    flexDirection: "row",
    justifyContent: "center",
  },
  spacer: {
    marginLeft: UNIT * 2,
    marginRight: UNIT * 2,
    color: colors.neutral400,
  },
} as const;

const Spacer = () => <Text style={styles.spacer}>|</Text>;

type ScreenLayoutProps = {
  breadcrumbs?: string[];
  persona: Persona;
  children: PropsWithChildren<{}>["children"];
};

export default function ScreenLayout(props: ScreenLayoutProps) {
  const [mobileMode, setMobileMode] = useRecoilState(mobileModeState);
  const [leftNavOpen, setLeftNavOpen] = useRecoilState<boolean>(
    leftNavOpenState
  );
  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const width = event.nativeEvent.layout.width;

    setMobileMode(width < MOBILE_WIDTH_THRESHOLD);
  }, []);

  const toggleLeftNav = useCallback(() => setLeftNavOpen((open) => !open), []);

  const route = useRoute<any>();

  const noScrollParam = route.params?.noScroll;
  const style =
    noScrollParam === "true"
      ? { ...styles.screen, height: "unset" }
      : styles.screen;

  const screenExtraStyles =
    mobileMode && leftNavOpen ? { overflow: "hidden" as const } : {};

  return (
    <View style={style} onLayout={onLayout}>
      <View key="header" style={styles.headerContainer}>
        <Header onToggleMenu={toggleLeftNav} />
      </View>
      <View key="body" style={{ ...styles.screenBody, ...screenExtraStyles }}>
        {!mobileMode || leftNavOpen ? (
          <LeftNav persona={props.persona} />
        ) : null}
        <View style={mobileMode ? styles.contentMobile : styles.content}>
          <Text style={styles.breadcrumbs}>
            {(props.breadcrumbs || []).join(" > ")}
          </Text>
          <ScrollView contentContainerStyle={styles.contentScroll}>
            <View key="content" style={{ flex: 1, minHeight: "max-content" }}>
              {props.children}
            </View>
            {!mobileMode && (
              <View key="footer" style={styles.footer}>
                {footerElements().map((element, index, all) => (
                  <Fragment key={`footer${index}`}>
                    {element}
                    {index < all.length - 1 && <Spacer />}
                  </Fragment>
                ))}
              </View>
            )}
          </ScrollView>
        </View>
      </View>
    </View>
  );
}
